// ============================================================================
// Cover Letter API - AI-powered cover letter generation
// Generates personalized cover letters using LLM APIs
// ============================================================================

import { coverLetterRepository, applicationRepository, profileRepository } from '../storage/db'
import { aiCompletionTransport } from './aiCompletionTransport'
import type { CoverLetter, CoverLetterTone, UserProfile, JobApplication } from '../types'

// ============================================================================
// Types
// ============================================================================

export interface GenerateCoverLetterInput {
  applicationId: string
  jobDescription: string
  tone: CoverLetterTone
}

export interface GenerateCoverLetterResult {
  coverLetter: CoverLetter
  generatedContent: string
}

// ============================================================================
// Prompt Building
// ============================================================================

function buildProfileSummary(profile: UserProfile): string {
  const { personal, workExperience, education, skills } = profile
  const name = `${personal.firstName} ${personal.lastName}`.trim()
  const location = [personal.location.city, personal.location.state].filter(Boolean).join(', ')

  let summary = `Candidate: ${name || 'Not specified'}`
  if (personal.email) summary += `\nEmail: ${personal.email}`
  if (personal.phone) summary += `\nPhone: ${personal.phone}`
  if (location) summary += `\nLocation: ${location}`
  if (personal.linkedIn) summary += `\nLinkedIn: ${personal.linkedIn}`
  if (personal.portfolio) summary += `\nPortfolio: ${personal.portfolio}`

  if (workExperience.length > 0) {
    summary += '\n\nWork Experience:'
    for (const exp of workExperience) {
      const dates = exp.endDate
        ? `${exp.startDate} - ${exp.endDate}`
        : `${exp.startDate} - Present`
      summary += `\n- ${exp.title} at ${exp.company} (${dates})`
      if (exp.description) summary += `\n  ${exp.description}`
    }
  }

  if (education.length > 0) {
    summary += '\n\nEducation:'
    for (const edu of education) {
      summary += `\n- ${edu.degree} in ${edu.field} from ${edu.institution} (${edu.graduationDate})`
    }
  }

  if (skills.length > 0) {
    summary += `\n\nSkills: ${skills.join(', ')}`
  }

  return summary
}

function buildToneInstruction(tone: CoverLetterTone): string {
  switch (tone) {
    case 'professional':
      return 'Write in a formal, professional tone. Use traditional business language, maintain respectful distance, and emphasize qualifications and achievements with concrete examples.'
    case 'conversational':
      return 'Write in a warm, conversational tone that feels natural and approachable. Be engaging while still professional, as if speaking to a future colleague. Show personality.'
    case 'creative':
      return 'Write in a creative, compelling tone that stands out. Use vivid language, tell a brief story or use an engaging hook. Be memorable while remaining appropriate for a professional context.'
  }
}

function buildPrompt(
  profileSummary: string,
  application: JobApplication,
  jobDescription: string,
  tone: CoverLetterTone
): string {
  const toneInstruction = buildToneInstruction(tone)

  return `You are an expert cover letter writer. Generate a personalized cover letter based on the following information.

${toneInstruction}

CANDIDATE PROFILE:
${profileSummary}

TARGET POSITION:
Company: ${application.company}
Position: ${application.position}

JOB DESCRIPTION:
${jobDescription}

REQUIREMENTS:
1. Address the specific role and company
2. Highlight relevant experience from the candidate's profile that matches the job requirements
3. Keep it concise (3-4 paragraphs, around 300-400 words)
4. Include a strong opening that captures attention
5. Demonstrate knowledge of the role requirements
6. End with a clear call to action
7. Do NOT use placeholders like [Your Name] - use the actual candidate name from the profile, or "I" if the name is not available
8. Output ONLY the cover letter text, no preamble or explanation`
}

// ============================================================================
// Cover Letter API
// ============================================================================

export const coverLetterApi = {
  /**
   * Generate a cover letter using AI
   */
  async generate(input: GenerateCoverLetterInput): Promise<GenerateCoverLetterResult> {
    const application = await applicationRepository.getById(input.applicationId)
    if (!application) {
      throw new Error(`Application ${input.applicationId} not found`)
    }

    const profile = await profileRepository.get()
    if (!profile) {
      throw new Error('User profile not found. Please create a profile first.')
    }

    const profileSummary = buildProfileSummary(profile)
    const prompt = buildPrompt(profileSummary, application, input.jobDescription, input.tone)
    const generatedContent = await aiCompletionTransport.completeCoverLetterPrompt(prompt)

    // Check if a cover letter already exists for this application
    const existing = await coverLetterRepository.getByApplicationId(input.applicationId)

    let coverLetter: CoverLetter
    if (existing) {
      existing.content = generatedContent
      existing.tone = input.tone
      existing.jobDescription = input.jobDescription
      await coverLetterRepository.save(existing)
      coverLetter = existing
    } else {
      const id = await coverLetterRepository.create({
        applicationId: input.applicationId,
        content: generatedContent,
        tone: input.tone,
        jobDescription: input.jobDescription,
      })
      const created = await coverLetterRepository.get(id)
      coverLetter = created!

      // Link the cover letter to the application
      await applicationRepository.save({
        ...application,
        coverLetterId: id,
      })
    }

    return { coverLetter, generatedContent }
  },

  /**
   * Get cover letter for an application
   */
  async getByApplicationId(applicationId: string): Promise<CoverLetter | undefined> {
    return coverLetterRepository.getByApplicationId(applicationId)
  },

  /**
   * Get cover letter by ID
   */
  async getById(id: string): Promise<CoverLetter | undefined> {
    return coverLetterRepository.get(id)
  },

  /**
   * Update cover letter content (after user edits)
   */
  async updateContent(id: string, content: string): Promise<CoverLetter> {
    const letter = await coverLetterRepository.get(id)
    if (!letter) {
      throw new Error(`Cover letter ${id} not found`)
    }
    letter.content = content
    await coverLetterRepository.save(letter)
    return letter
  },

  /**
   * Delete a cover letter
   */
  async delete(id: string): Promise<void> {
    await coverLetterRepository.delete(id)
  },

  /**
   * Check if AI is configured
   */
  isConfigured(): boolean {
    return aiCompletionTransport.isAvailable()
  },

  /**
   * Label for UI (legacy client model, or default — server may differ when using Edge).
   */
  getModel(): string {
    return aiCompletionTransport.modelLabel()
  },
}
